import { PlusOutlined } from "@ant-design/icons";
import {
  TableProps,
  Flex,
  Typography,
  Button,
  Table,
  InputRef,
  Card,
} from "antd";
import { getAllProductsByBusinessId } from "hooks/queries/product";
import { PromoStatusBadge } from "pages/products/component/PromoStatusBadge";
import { CSSProperties, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import PATHS from "shared/constants/paths";
import { PAGE_SIZE } from "shared/constants";
import { translate } from "shared/languages";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { colors } from "shared/theme/color";
import { PackageProduct, ProductType } from "shared/types";
import { formatCurrency, formatTierValidity } from "shared/utils";
import { textPx } from "shared/constants/zoom";

// Minimalist uppercase ink-3 column header (prototype `table.tbl th`).
const thStyle: CSSProperties = {
  textTransform: "uppercase",
  fontSize: textPx(11),
  fontWeight: 600,
  letterSpacing: "0.06em",
  color: colors.ink3,
};
const th = (label: string) => <span style={thStyle}>{label}</span>;
const tnum: CSSProperties = { fontVariantNumeric: "tabular-nums" };

interface ClassTypePackageProps {
  classTypeId: string;
  // Which catalog slice this tab shows; Paket and Membership are separate
  // tabs on the class detail, so no Tipe column is needed.
  type: "credit" | "membership";
}

// Catalog products of one type that sell access to this class. Rows link to
// the product detail pages; creation pre-attaches the class.
const ClassTypePackage = ({ classTypeId, type }: ClassTypePackageProps) => {
  const navigate = useNavigate();
  const { isRestricted } = useAdminCapabilities();
  const catalogRestricted = isRestricted(AdminCapability.CatalogPricing);
  const location = useLocation();
  const searchInput = useRef<InputRef>(null);
  const isMembership = type === "membership";
  const { data, isLoading } = getAllProductsByBusinessId({
    class_type_id: classTypeId,
  });
  const products = useMemo(
    () =>
      (data?.products ?? []).filter((p) =>
        isMembership
          ? p.product_type === ProductType.Membership
          : p.product_type === ProductType.Credit,
      ),
    [data, isMembership],
  );

  const classCreditCost = (product: PackageProduct) =>
    product.class_type.find((c) => c.id === classTypeId)?.credit_cost ?? 1;

  const priceRange = (min: number | null, max: number | null) => {
    if (min === null || max === null) return "-";
    return min === max
      ? formatCurrency(min)
      : `${formatCurrency(min)} - ${formatCurrency(max)}`;
  };

  const nameColumn: NonNullable<TableProps<PackageProduct>["columns"]>[number] = {
    title: th(translate("label.name")),
    dataIndex: "name",
    key: "name",
    render: (_, product) => (
      <Flex gap={8} align="center" wrap="wrap">
        <span style={{ fontWeight: 600 }}>{product.name}</span>
        <PromoStatusBadge product={product} />
      </Flex>
    ),
    ...getColumnSearchProps({ config: "name", searchInput }),
  };

  const creditColumns: TableProps<PackageProduct>["columns"] = [
    nameColumn,
    {
      title: th(translate("product_catalog.col_tiers")),
      dataIndex: "tier_count",
      key: "tier_count",
      align: "right",
      render: (_, product) => (
        <span style={tnum}>{product.tier_count}</span>
      ),
      sorter: (a, b) => a.tier_count - b.tier_count,
    },
    {
      // What one booking of THIS class takes on the product (every tier).
      title: th(translate("label.credits")),
      key: "credit_cost",
      align: "right",
      render: (_, product) => (
        <span style={tnum}>{classCreditCost(product)}</span>
      ),
      sorter: (a, b) => classCreditCost(a) - classCreditCost(b),
    },
    {
      title: th(translate("product_catalog.col_price_range")),
      dataIndex: "price_min",
      key: "price_range",
      align: "right",
      render: (_, product) => (
        <span style={tnum}>{priceRange(product.price_min, product.price_max)}</span>
      ),
      sorter: (a, b) => (a.price_min ?? 0) - (b.price_min ?? 0),
    },
  ];

  const membershipColumns: TableProps<PackageProduct>["columns"] = [
    nameColumn,
    {
      title: th(translate("label.price")),
      dataIndex: "price_min",
      key: "price",
      align: "right",
      render: (_, product) => (
        <span style={tnum}>
          {product.tiers[0] ? formatCurrency(product.tiers[0].price) : "-"}
        </span>
      ),
      sorter: (a, b) => (a.price_min ?? 0) - (b.price_min ?? 0),
    },
    {
      title: th(translate("label.validity_period")),
      dataIndex: "tiers",
      key: "validity",
      align: "right",
      render: (_, product) => (
        <span style={tnum}>
          {product.tiers[0] ? formatTierValidity(product.tiers[0]) : "-"}
        </span>
      ),
    },
  ];

  return (
    <Card>
      <Flex vertical gap={18}>
        <Flex justify="space-between" align="flex-start" gap={16}>
          <Flex vertical>
            <Typography.Title
              level={5}
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: textPx(17),
                letterSpacing: "-0.01em",
              }}
            >
              {translate(
                isMembership
                  ? "product_catalog.class_memberships_title"
                  : "title.class_packages",
              )}
            </Typography.Title>
            <Typography.Text
              style={{ color: colors.ink2, fontSize: textPx(13), marginTop: 4 }}
            >
              {translate(
                isMembership
                  ? "product_catalog.class_memberships_subtitle"
                  : "subtitle.class_packages",
              )}
            </Typography.Text>
          </Flex>
          <Button
            // Both open the create page with this class preselected;
            // returnTo brings the admin back to this tab afterwards.
            onClick={() =>
              navigate(
                isMembership
                  ? `${PATHS.MEMBERSHIP_NEW}?class_id=${classTypeId}`
                  : `${PATHS.PACKAGE_NEW}?type=credit&class_id=${classTypeId}`,
                { state: { returnTo: `${location.pathname}${location.search}` } },
              )
            }
            type="primary"
            icon={<PlusOutlined />}
            disabled={catalogRestricted}
          >
            {isMembership
              ? translate("product_catalog.add_membership")
              : `${translate("button.add")} ${translate("package")}`}
          </Button>
        </Flex>
        <Table
          showSorterTooltip={false}
          rowKey="id"
          size={"middle"}
          loading={isLoading}
          dataSource={products}
          columns={isMembership ? membershipColumns : creditColumns}
          onRow={(record) => {
            return {
              onClick: () =>
                // returnTo carries this class page (+ its active tab) so
                // package-detail's Simpan Perubahan / Hapus / back-arrow
                // can land back HERE instead of the main Paket/Membership
                // catalog — see the "returnTo" gotcha in package-detail.
                navigate(
                  `${isMembership ? PATHS.MEMBERSHIP : PATHS.PACKAGE}/${record.id}`,
                  { state: { returnTo: `${location.pathname}${location.search}` } },
                ),
              style: { cursor: "pointer" },
            };
          }}
          pagination={{
            pageSize: PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </Flex>
    </Card>
  );
};
export default ClassTypePackage;
